import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AuthAdapter, AuthSession, AuthUser } from './auth-adapter';
import { ErrorScreen, LoadingScreen } from './loading-screen';

export interface AuthGateProps {
  readonly adapter: AuthAdapter;
  readonly children: ReactNode;
}

interface AuthContextValue {
  readonly isLoggedIn: boolean;
  readonly user: AuthUser | null;
  readonly sessionToken: string | null;
  readonly firstTimeLogin: boolean;
  readonly markHowToPlaySeen: () => void;
  readonly logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthGate');
  return ctx;
};

let inMemoryToken: string | null = null;
let pendingJwt: string | null = null;
let onUnauthorizedCallback: (() => void) | null = null;

export const getSessionToken = (): string | null => inMemoryToken;

export const isAuthenticated = (): boolean => inMemoryToken !== null;

export const handleUnauthorized = (): void => {
  inMemoryToken = null;
  onUnauthorizedCallback?.();
};

type GateState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'authenticated'; session: AuthSession };

export const LOADING_SCREEN_MIN_DURATION_MS = 2_500;

const waitForRemainingLoadTime = (
  loadStartedAt: number,
  registerTimer: (timerId: ReturnType<typeof setTimeout>) => void,
): Promise<void> => {
  const remaining = LOADING_SCREEN_MIN_DURATION_MS - (Date.now() - loadStartedAt);
  if (remaining <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    registerTimer(setTimeout(resolve, remaining));
  });
};

export const AuthGate = ({ adapter, children }: AuthGateProps) => {
  const [state, setState] = useState<GateState>({ status: 'idle' });
  const [howToPlaySeen, setHowToPlaySeen] = useState(false);

  useEffect(() => {
    onUnauthorizedCallback = () => setState({ status: 'error', message: 'Session expired' });
    return () => {
      onUnauthorizedCallback = null;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let minDurationTimer: ReturnType<typeof setTimeout> | undefined;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token') ?? pendingJwt;

    if (!token) {
      setState({ status: 'error', message: 'No authentication token provided' });
      return;
    }

    pendingJwt = token;

    // Intentionally keep ?token= in the URL. The operator reloads the iframe
    // from its own URL when the modal is closed and reopened; the in-memory
    // token does not survive that reload, so the URL is the only credential
    // available to re-authenticate. Stripping it broke close/reopen. See CAE-XXX.
    const loadStartedAt = Date.now();
    setState({ status: 'loading' });

    void adapter.exchangeCode(token).then(
      async (session) => {
        await waitForRemainingLoadTime(loadStartedAt, (timerId) => {
          minDurationTimer = timerId;
        });
        if (cancelled) return;

        pendingJwt = null;
        inMemoryToken = session.sessionToken;
        setState({ status: 'authenticated', session });
      },
      (err) => {
        if (cancelled) return;

        setState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Authentication failed',
        });
      },
    );

    return () => {
      cancelled = true;
      if (minDurationTimer !== undefined) clearTimeout(minDurationTimer);
    };
  }, [adapter]);

  const logout = useMemo(
    () => async () => {
      if (inMemoryToken) {
        await adapter.logout(inMemoryToken).catch(() => {});
      }
      inMemoryToken = null;
      pendingJwt = null;
      setState({ status: 'error', message: 'Session ended' });
    },
    [adapter],
  );

  if (state.status === 'error') {
    return <ErrorScreen message={state.message} />;
  }

  if (state.status === 'authenticated') {
    const ctxValue: AuthContextValue = {
      isLoggedIn: true,
      user: state.session.user,
      sessionToken: state.session.sessionToken,
      firstTimeLogin: (state.session.firstTimeLogin ?? false) && !howToPlaySeen,
      markHowToPlaySeen: () => setHowToPlaySeen(true),
      logout,
    };
    return <AuthContext value={ctxValue}>{children}</AuthContext>;
  }

  return <LoadingScreen />;
};
